// @flow
import {action, computed, observable} from 'mobx'
import {notification} from 'antd'
import {singleDefaults as defaults, saveDefaults} from './consts/defaults'
import {mergeDeep} from 'Utils'
import Validator from 'validatorjs'
import {Store} from 'Store'
import {Api} from 'Store/api'
import {Signal} from './signal'

export class Single {
  root: Store
  api: Api
  @observable signal: Signal = new Signal()
  @observable additional_signal: Signal = new Signal()
  @observable data: {[key: string]: any} = mergeDeep({}, defaults)
  @observable savedData = mergeDeep({}, saveDefaults)
  @observable loading = false
  @observable errors = {}

  constructor(rootStore: Store, api: Api) {
    this.root = rootStore
    this.api = api
  }

  @computed get submitEnabled() {
    const {stock_filter, trade_value, tickers} = this.data
    return trade_value && this.signal.text && (tickers.length || stock_filter?.id) && !this.loading && !this.errors.signal
  }

  @action blank() {
    this.data = mergeDeep({}, defaults)
    this.savedData = mergeDeep({}, saveDefaults)
    this.signal = new Signal()
    this.additional_signal = new Signal()
    this.errors = {}
  }

  @action change(key: string, value: any) {
    this.data[key] = value
  }

  @action selectStockFilter(id: number) {
    this.data.stock_filter = {id: id}
  }

  @action toggleAssetType() {
    const {savedData} = this
    const {has_coin, tickers, stock_filter, ...data} = this.data
    this.savedData = {tickers, stock_filter}
    this.data = {has_coin: !has_coin, ...data, ...savedData}
  }

  @action async load(id: number) {
    this.loading = true
    this.blank()

    try {
      let {tickers, ...data} = await this.api.request(`sys/algos/${id}`, 'get')
      tickers = tickers.length ? tickers.replace(/(,$)/g, '').split(', ') : []
      this.data = {tickers, ...data}
      this.signal = new Signal(data.signal)
      this.additional_signal = new Signal(data.additional_signal)
    } catch (e) {
      if (e.status_code === 404) {
        this.root.redirectTo({pathname: '/not-found'})
        return
      }
      notification['error']({
        message: 'Failed to load algo',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action async create() {
    const validation = this.validatePopup()
    if (validation.fails()) {
      this.errors = validation.errors.all()
      return
    }

    this.loading = true

    try {
      let {tickers, ...data} = await this.api.request('sys/algos', 'post', this.createBody())
      tickers = tickers.length ? tickers.replace(/(,$)/g, '').split(', ') : []
      this.data = {tickers, ...data}
      if (!this.root.algo.preparingBacktest) {
        this.root.redirectTo({
          pathname: '/manager'
        })
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to create algo',
        description: e.message || 'Unknown error'
      })
      if (!this.root.algo.preparingBacktest) {
        throw new Error()
      }
    } finally {
      this.loading = false
    }
  }

  @action async update(id: number = this.data.id) {
    this.loading = true
    try {
      await this.api.request(`sys/algos/${id}`, 'put', this.createBody())
      if (!this.root.algo.preparingBacktest && !this.root.backtest.editingAlgoLoading) {
        this.root.redirectTo({
          pathname: '/manager'
        })
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to update algo',
        description: e.message || 'Unknown error'
      })
      if (this.root.algo.preparingBacktest) {
        throw new Error()
      }
    } finally {
      this.loading = false
    }
  }

  @action createBody() {
    let body = mergeDeep({}, defaults)
    for (let prop in this.data) {
      if (this.data[prop] instanceof Array) {
        body[prop] = this.data[prop].concat()
      } else if (typeof this.data[prop] === 'object' && this.data[prop] !== null) {
        body[prop] = {...this.data[prop]}
      } else {
        body[prop] = this.data[prop]
      }
    }
    for (let prop in body) {
      if (body[prop] === '') {
        delete body[prop]
      }
    }
    if (body.stock_filter?.id === '') {
      delete body.stock_filter
    }
    body.tickers = body.tickers.join(', ')
    body.signal = this.signal.text
    body.additional_signal = this.additional_signal.text
    return body
  }

  @action validatePopup() {
    const rules = {
      name: 'required|max:1000'
    }
    const data = {
      name: this.data.name
    }
    return new Validator(data, rules)
  }

  @action removeError(name: string) {
    if (!this.errors[name]) {
      return
    }
    delete this.errors[name]
  }
}
